import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Pencil, Check, X } from 'lucide-react';
import { useDocumentsApi } from '../../hooks/useDocumentsApi';
import { toast } from 'react-hot-toast';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { get, set } from 'lodash';

interface DocumentDetailEditorProps {
  documentId?: string;
  metadata?: Record<string, any>;
  documentType?: string;
  onSaved?: (metadata: Record<string, any>) => void;
}

export const DocumentDetailEditor: React.FC<DocumentDetailEditorProps> = ({
  documentId,
  metadata,
  documentType,
  onSaved,
}) => {
  const { t } = useTranslation();
  const api = useDocumentsApi();
  const [editedMetadata, setEditedMetadata] = useState(metadata);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingCell, setEditingCell] = useState<{
    path: string;
    rowIndex?: number;
    colKey?: string;
  } | null>(null);

  // State for undo functionality
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [undoState, setUndoState] = useState<any>(null);
  const undoApplyRef = useRef<(() => void) | null>(null);
  const [currentToastId, setCurrentToastId] = useState<string | null>(null);

  const _docType = editedMetadata?.documentType || metadata?.documentType;

  useEffect(() => {
    setEditedMetadata(metadata);
    setEditingFieldId(null);
  }, [metadata]);

  const handleSaveWithUndo = (updatedMetadata: any, applyUndo?: () => void) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // If this is the first change in a batch, store a deep snapshot for undo
    if (!undoState) {
      const preChange = JSON.parse(JSON.stringify(editedMetadata ?? {}));
      setUndoState(preChange);
    }
    setEditedMetadata(updatedMetadata);
    // Keep a precise undo action for immediate field-level revert
    undoApplyRef.current = applyUndo || null;

    if (currentToastId) {
      toast.dismiss(currentToastId);
    }

    const newToastId = toast(
      (toastInstance) => (
        <div className="flex items-center">
          <span className="mr-4">{t('toast.changeSaved')}</span>
          <Button
            size="sm"
            onClick={() => {
              if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
              }
              if (undoApplyRef.current) {
                undoApplyRef.current();
              } else if (undoState && typeof undoState === 'object') {
                setEditedMetadata(undoState);
              }
              setUndoState(null); // Reset undo state
              undoApplyRef.current = null;
              toast.dismiss(toastInstance.id);
              setCurrentToastId(null);
            }}
          >
            {t('actions.undo')}
          </Button>
        </div>
      ),
      {
        duration: 5000,
        id: currentToastId || undefined,
      },
    );
    setCurrentToastId(newToastId);

    const newTimeoutId = setTimeout(() => {
      api
        .update(documentId!, { metadata: updatedMetadata })
        .then(() => api.get(documentId!))
        .then((doc) => {
          setEditedMetadata(doc?.metadata ?? updatedMetadata);
          toast.success(t('toast.updateSuccess'), { id: newToastId });
          setUndoState(null); // Clear undo state on successful save
          undoApplyRef.current = null;
          saveTimeoutRef.current = null;
          if (doc && doc.metadata && onSaved) onSaved(doc.metadata);
        })
        .catch(() => {
          toast.error(t('toast.updateError'), { id: newToastId });
          // On error, revert to the state before this batch of changes
          if (undoState) {
            setEditedMetadata(undoState);
          }
          setUndoState(null);
          undoApplyRef.current = null;
          saveTimeoutRef.current = null;
        });
      setCurrentToastId(null);
    }, 5000);

    saveTimeoutRef.current = newTimeoutId;
  };

  const _handleSave = (path: string, value: any) => {
    const updatedMetadata = { ...editedMetadata };
    set(updatedMetadata, path, value);
    handleSaveWithUndo(updatedMetadata);
    setEditingCell(null);
  };

  const _handleTableSave = () => {
    if (editingCell) {
      const { path, rowIndex, colKey } = editingCell;
      const tableData = get(editedMetadata, path);
      if (Array.isArray(tableData) && rowIndex !== undefined && colKey !== undefined) {
        // Here, we assume the value is already updated in editedMetadata
        // This part of the logic might need adjustment based on how cell values are temporarily stored
      }
    }
    handleSaveWithUndo(editedMetadata);
    setEditingCell(null);
  };

  const _handleTableUpdate = (path: string, rowIndex: number, colKey: string, value: any) => {
    const tableData = get(editedMetadata, path);
    if (Array.isArray(tableData) && rowIndex !== undefined && colKey !== undefined) {
      const updatedTableData = [...tableData];
      updatedTableData[rowIndex] = {
        ...updatedTableData[rowIndex],
        [colKey]: value,
      };
      const updatedMetadata = { ...editedMetadata };
      set(updatedMetadata, path, updatedTableData);
      setEditedMetadata(updatedMetadata);
    }
  };

  const _startTableEditing = (path: string) => {
    setEditingCell({ path });
  };

  const _cancelTableEditing = () => {
    // Revert any changes made during table editing
    // This might require storing a temporary version of the table data
    setEditingCell(null);
  };

  const _saveTableChanges = (_path: string) => {
    handleSaveWithUndo(editedMetadata);
    setEditingCell(null);
  };

  const cloneDeep = (obj: any) => JSON.parse(JSON.stringify(obj ?? {}));
  const getBaseByPath = (root: any, path: string[]) =>
    path.reduce(
      (acc: any, key: string) => (acc && typeof acc === 'object' ? acc[key] : undefined),
      root,
    );
  const setByPath = (root: any, path: string[], key: string, value: any) => {
    const next = cloneDeep(root);
    let base = next as any;
    for (const p of path) {
      if (!base[p] || typeof base[p] !== 'object') base[p] = {};
      base = base[p];
    }
    base[key] = value;
    return next;
  };

  const _saveMetadata = async (next: Record<string, any>) => {
    if (!documentId) {
      toast.error('Document introuvable');
      return;
    }
    // This function will now use the undo flow
    handleSaveWithUndo(next);
  };
  const safeParseJson = (value: unknown): unknown => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return value;
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  };

  const isIsoDate = (val: string): boolean => {
    // Simple ISO date detection
    return /^(\d{4}-\d{2}-\d{2})(T|$)/.test(val);
  };

  const tryParseDate = (val?: string): Date | undefined => {
    if (!val) return undefined;
    const d = new Date(val);
    return isNaN(d.getTime()) ? undefined : d;
  };

  const formatDateLikeSample = (sample: string | undefined, date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = date.getUTCFullYear();
    const mm = pad(date.getUTCMonth() + 1);
    const dd = pad(date.getUTCDate());
    const HH = pad(date.getUTCHours());
    const MM = pad(date.getUTCMinutes());
    const SS = pad(date.getUTCSeconds());
    const mmm = String(date.getUTCMilliseconds()).padStart(3, '0');
    if (!sample) return date.toISOString();
    if (/^\d{4}-\d{2}-\d{2}$/.test(sample)) return `${yyyy}-${mm}-${dd}`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(sample))
      return `${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}Z`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(sample))
      return `${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}.${mmm}Z`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(sample))
      return `${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}`;
    return date.toISOString();
  };

  const humanizeKey = (key: string): string =>
    key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^\w/, (c) => c.toUpperCase());

  const looksLikeMoneyKey = (key: string): boolean =>
    /(amount|total|subtotal|price|tax|fee)/i.test(key);

  const formatCurrency = (n: number): string =>
    n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const buildColumns = (rows: Array<Record<string, any>>, preferredOrder: string[]): string[] => {
    const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r || {}))));
    const ordered = preferredOrder.filter((k) => keys.includes(k));
    const remaining = keys.filter((k) => !ordered.includes(k));
    return [...ordered, ...remaining].slice(0, 10);
  };

  const renderDataTable = (
    key: string,
    rows: Array<Record<string, any>>,
    preferredOrder: string[],
    headerMap: Record<string, string>,
  ) => {
    if (!rows || rows.length === 0) return <span className="text-muted-foreground">—</span>;
    const cols = buildColumns(rows, preferredOrder);
    return (
      <div className="border rounded-lg bg-white shadow-sm w-full max-w-full overflow-x-auto">
        <div className="overflow-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/30 backdrop-blur z-10">
              <tr>
                {cols.map((c) => (
                  <th key={c} className="text-left font-semibold p-3 whitespace-nowrap">
                    {headerMap[c] || humanizeKey(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="odd:bg-muted/10">
                  {cols.map((c) => {
                    const v = (row as any)[c];
                    const node =
                      typeof v === 'number' ? (
                        <span className="tabular-nums">{formatCurrency(v)}</span>
                      ) : (
                        (formatValue(c, v) as any)
                      );
                    const isRight = typeof v === 'number' || looksLikeMoneyKey(c);
                    return (
                      <td
                        key={c}
                        className={`p-3 align-top ${isRight ? 'text-right' : 'text-left'}`}
                      >
                        {node}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Editable table for arrays in Invoice (Line_Items, Tax_Lines)
  const [editingTableId, setEditingTableId] = React.useState<string | null>(null);
  const [tableDraftRows, setTableDraftRows] = React.useState<Array<Record<string, any>>>([]);

  const renderEditableArrayTable = (
    title: string,
    rows: Array<Record<string, any>>,
    preferredOrder: string[],
    headerMap: Record<string, string>,
    source: { path: string[]; key: string },
  ) => {
    if (!Array.isArray(rows)) return null;
    const isEditing = editingTableId === `${[...source.path, source.key].join('.')}`;
    const cols = buildColumns(rows, preferredOrder);

    const currentRows = isEditing ? tableDraftRows : rows;

    return (
      <div className="border rounded-lg bg-white shadow-sm w-full max-w-full overflow-x-auto relative">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/10">
          <div className="font-medium">{title}</div>
          {!isEditing ? (
            <button
              className="text-xs px-2 py-1 rounded border"
              onClick={() => {
                setEditingTableId(`${[...source.path, source.key].join('.')}`);
                setTableDraftRows(JSON.parse(JSON.stringify(rows)));
              }}
            >
              Modifier
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                className="text-xs px-2 py-1 rounded border bg-green-600 text-white"
                onClick={async () => {
                  const prevRows = rows;
                  const next = setByPath(editedMetadata, source.path, source.key, tableDraftRows);
                  setEditingTableId(null);
                  try {
                    handleSaveWithUndo(next, () => {
                      setEditedMetadata((prev) =>
                        setByPath(prev, source.path, source.key, prevRows),
                      );
                    });
                  } catch {
                    /* handled */
                  }
                }}
              >
                Enregistrer
              </button>
              <button
                className="text-xs px-2 py-1 rounded border bg-rose-600 text-white"
                onClick={() => {
                  setEditingTableId(null);
                  setTableDraftRows([]);
                }}
              >
                Annuler
              </button>
            </div>
          )}
        </div>
        <div className="overflow-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/30 backdrop-blur z-10">
              <tr>
                {cols.map((c) => (
                  <th key={c} className="text-left font-semibold p-3 whitespace-nowrap">
                    {headerMap[c] || humanizeKey(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.map((row, rIdx) => (
                <tr key={rIdx} className="odd:bg-muted/10">
                  {cols.map((c) => {
                    const v = (row as any)[c];
                    const right = typeof v === 'number' || looksLikeMoneyKey(c);
                    return (
                      <td key={c} className={`p-3 align-top ${right ? 'text-right' : 'text-left'}`}>
                        {isEditing ? (
                          <input
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={v == null ? '' : String(v)}
                            onChange={(e) => {
                              const val = e.target.value;
                              setTableDraftRows((prev) => {
                                const copy = JSON.parse(JSON.stringify(prev));
                                copy[rIdx][c] = val;
                                return copy;
                              });
                            }}
                          />
                        ) : typeof v === 'number' ? (
                          <span className="tabular-nums">{formatCurrency(v)}</span>
                        ) : (
                          (formatValue(c, v) as any)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const formatValue = (key: string, val: any): React.ReactNode => {
    if (val === null || val === undefined || val === 'null')
      return <span className="text-muted-foreground">—</span>;

    // Parse embedded JSON strings
    const parsed = safeParseJson(val);

    if (typeof parsed === 'string') {
      if (isIsoDate(parsed)) {
        const d = new Date(parsed);
        return <span>{d.toLocaleDateString()}</span>;
      }
      return <span className="break-words">{parsed}</span>;
    }

    if (typeof parsed === 'number') {
      if (looksLikeMoneyKey(key)) {
        return (
          <span>
            {parsed.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      }
      return <span>{parsed}</span>;
    }

    if (typeof parsed === 'boolean') {
      return (
        <span
          className={
            parsed
              ? 'text-green-700 bg-green-100 px-2 py-0.5 rounded'
              : 'text-amber-700 bg-amber-100 px-2 py-0.5 rounded'
          }
        >
          {parsed ? 'Oui' : 'Non'}
        </span>
      );
    }

    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return <span className="text-muted-foreground">—</span>;
      if (typeof parsed[0] === 'object') {
        // Specialized big tables based on key
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes('transaction')) {
          const preferred = [
            'date',
            'value_date',
            'description',
            'debit',
            'credit',
            'amount',
            'balance',
            'type',
          ];
          const headers: Record<string, string> = {
            date: 'Date',
            value_date: 'Valeur',
            description: 'Libellé',
            debit: 'Débit',
            credit: 'Crédit',
            amount: 'Montant',
            balance: 'Solde',
            type: 'Type',
          };
          return renderDataTable(key, parsed as any, preferred, headers);
        }
        if (lowerKey.includes('item') || lowerKey.includes('ligne')) {
          const preferred = [
            'reference',
            'sku',
            'description',
            'qty',
            'unit',
            'unit_price',
            'price',
            'tax',
            'discount',
            'amount',
          ];
          const headers: Record<string, string> = {
            reference: 'Référence',
            sku: 'SKU',
            description: 'Désignation',
            qty: 'Qté',
            unit: 'Unité',
            unit_price: 'Prix unitaire',
            price: 'Prix',
            tax: 'Taxe',
            discount: 'Remise',
            amount: 'Montant',
          };
          return renderDataTable(key, parsed as any, preferred, headers);
        }
        // Generic compact table fallback
        return renderDataTable(key, parsed as any, [], {});
      }
      // Array of primitives → badge list
      return (
        <div className="flex flex-wrap gap-1">
          {parsed.map((item, idx) => (
            <span key={idx} className="px-2 py-0.5 rounded bg-muted/40 text-xs">
              {String(item)}
            </span>
          ))}
        </div>
      );
    }

    if (typeof parsed === 'object') {
      const objEntries = Object.entries(parsed as Record<string, any>);
      if (objEntries.length === 0) return <span className="text-muted-foreground">—</span>;
      return (
        <div className="grid grid-cols-2 gap-2 p-2 rounded bg-muted/10">
          {objEntries.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <div className="text-[11px] text-muted-foreground truncate" title={humanizeKey(k)}>
                {humanizeKey(k)}
              </div>
              <div className="text-sm break-words">{formatValue(k, v)}</div>
            </div>
          ))}
        </div>
      );
    }

    // Fallback
    return <span className="break-words break-all">{String(parsed)}</span>;
  };

  const entries: Array<{ key: string; value: any }> = React.useMemo(() => {
    if (!editedMetadata || typeof editedMetadata !== 'object') return [];
    return Object.entries(editedMetadata).map(([key, value]) => ({
      key,
      value,
    }));
  }, [editedMetadata]);

  const _isLargeContent = (key: string, val: any): boolean => {
    const parsed = safeParseJson(val);
    if (Array.isArray(parsed)) return true;
    if (typeof parsed === 'object' && parsed !== null) {
      return Object.keys(parsed).length > 6;
    }
    if (typeof parsed === 'string') return parsed.length > 60 || /\n/.test(parsed);
    return false;
  };

  // ---------- Schema-aware layouts ----------
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const hasTokens = (key: string, tokens: string[]) =>
    tokens.every((t) => normalize(key).includes(normalize(t)));

  const getFirst = (obj: Record<string, any>, tokensList: string[][]): any => {
    // search current object
    for (const [k, v] of Object.entries(obj)) {
      for (const tokens of tokensList) {
        if (hasTokens(k, tokens)) return v;
      }
    }
    // search common nested container like `data`
    if (obj && typeof obj === 'object' && obj['data'] && typeof obj['data'] === 'object') {
      const nested = obj['data'] as Record<string, any>;
      for (const [k, v] of Object.entries(nested)) {
        for (const tokens of tokensList) {
          if (hasTokens(k, tokens)) return v;
        }
      }
    }
    return undefined;
  };

  const resolveFirstSource = (
    obj: Record<string, any>,
    tokensList: string[][],
  ): { path: string[]; key: string } | undefined => {
    // Look at current object
    for (const [k] of Object.entries(obj)) {
      for (const tokens of tokensList) {
        if (hasTokens(k, tokens)) return { path: [], key: k };
      }
    }
    // Look at nested 'data'
    if (obj && typeof obj === 'object' && obj['data'] && typeof obj['data'] === 'object') {
      const nested = obj['data'] as Record<string, any>;
      for (const [k] of Object.entries(nested)) {
        for (const tokens of tokensList) {
          if (hasTokens(k, tokens)) return { path: ['data'], key: k };
        }
      }
    }
    return undefined;
  };

  const _composeAddress = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      const parts: string[] = [];
      const push = (k: string) => {
        const v = value[k] ?? value[k.toLowerCase()] ?? value[k.replace(/\s+/g, '_')];
        if (v) parts.push(String(v));
      };
      [
        'street',
        'address',
        'address_line',
        'city',
        'region',
        'state',
        'postal_code',
        'zipcode',
        'country',
      ].forEach(push);
      return parts.filter(Boolean).join(', ');
    }
    return String(value);
  };

  const renderKeyValue = (
    label: string,
    value: any,
    required = false,
    source?: { path: string[]; key: string },
  ) => {
    const isEmpty =
      value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    const isRequiredEmpty = required && isEmpty;
    const fieldId = source ? `${[...source.path, source.key].join('.')}` : undefined;
    const content = isEmpty ? (
      <span className={isRequiredEmpty ? 'text-red-700' : 'text-muted-foreground'}>—</span>
    ) : React.isValidElement(value) ? (
      (value as React.ReactElement)
    ) : (
      (formatValue(source?.key || label, value) as React.ReactNode)
    );
    const isDateField = typeof value === 'string' && isIsoDate(value);
    return (
      <div
        className={`grid gap-1 min-w-0 group relative ${
          isRequiredEmpty ? 'bg-red-50 border border-red-200 rounded p-2' : ''
        }`}
      >
        <Label className={`truncate ${isRequiredEmpty ? 'text-red-700' : ''}`} title={label}>
          {label}
        </Label>
        {editingFieldId === fieldId ? (
          <div className="flex items-center gap-2">
            {isDateField ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button className="px-2 py-1 h-8 border rounded text-sm bg-white">
                    {editingValue || value || 'Sélectionner la date'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={tryParseDate(editingValue || (value as string))}
                    onSelect={(d: Date | undefined) => {
                      if (!d) return;
                      const formatted = formatDateLikeSample(value as string, d);
                      setEditingValue(formatted);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <input
                className="w-full border rounded px-2 py-1 text-sm"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
              />
            )}
            <button
              type="button"
              className="p-1 rounded hover:bg-green-100 text-green-700"
              onClick={async (e) => {
                e.preventDefault();
                if (!source) return setEditingFieldId(null);
                const baseBefore = getBaseByPath(editedMetadata, source.path) || {};
                const prevVal = baseBefore[source.key];
                const next = setByPath(editedMetadata, source.path, source.key, editingValue);
                setEditingFieldId(null);
                try {
                  handleSaveWithUndo(next, () => {
                    setEditedMetadata((prev) => setByPath(prev, source.path, source.key, prevVal));
                  });
                } catch {
                  /* handled in save */
                }
              }}
              title="Enregistrer"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="p-1 rounded hover:bg-rose-100 text-rose-700"
              onClick={(e) => {
                e.preventDefault();
                setEditingFieldId(null);
              }}
              title="Annuler"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div
            className={`min-w-0 text-sm break-words pr-6 ${isRequiredEmpty ? 'text-red-700' : ''}`}
          >
            {content}
          </div>
        )}
        {source && editingFieldId !== fieldId && (
          <button
            type="button"
            aria-label="Edit field"
            className="absolute top-0 right-0 p-1 rounded hover:bg-accent text-muted-foreground opacity-0 group-hover:opacity-100 transition"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setEditingFieldId(fieldId!);
              const base = getBaseByPath(editedMetadata, source.path) || {};
              const currentVal = base[source.key];
              setEditingValue(currentVal == null ? '' : String(currentVal));
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  };

  const renderInvoiceLayout = (data: Record<string, any>): React.ReactNode => {
    // Prefer nested metadata.data if present
    const inv: Record<string, any> =
      data &&
      typeof data === 'object' &&
      (data as any).data &&
      typeof (data as any).data === 'object'
        ? (data as any).data
        : data;
    const basePath =
      data &&
      typeof data === 'object' &&
      (data as any).data &&
      typeof (data as any).data === 'object'
        ? ['data']
        : [];

    const requiredKeys = new Set<string>([
      'Invoice_Number',
      'Date',
      'Due_Date',
      'Vendor_Name',
      'Vendor_Address',
      'Bill_To_Name',
      'Bill_To_Address',
      'Subtotal',
      'Tax',
      'Total',
      'Balance_Due',
      'Currency_Code',
      'Document_Type',
    ]);
    const req = (k: string) => requiredKeys.has(k);

    const vendorAddress = [
      inv['Vendor_Address'],
      inv['Vendor_City'],
      inv['Vendor_State'],
      inv['Vendor_Zipcode'],
      inv['Vendor_Country'],
    ]
      .filter(Boolean)
      .join(', ');
    const billToAddress = [
      inv['Bill_To_Address'],
      inv['Bill_To_City'],
      inv['Bill_To_State'],
      inv['Bill_To_Zipcode'],
    ]
      .filter(Boolean)
      .join(', ');

    const header = (
      <div className="grid grid-cols-1 gap-4 w-full max-w-full">
        {renderKeyValue('Invoice Number', inv['Invoice_Number'], req('Invoice_Number'), {
          path: basePath,
          key: 'Invoice_Number',
        })}
        {renderKeyValue('Invoice Date', inv['Date'], req('Date'), {
          path: basePath,
          key: 'Date',
        })}
        {renderKeyValue('Due Date', inv['Due_Date'], req('Due_Date'), {
          path: basePath,
          key: 'Due_Date',
        })}
        {renderKeyValue('Purchase Order Number', inv['PO_Number'], false, {
          path: basePath,
          key: 'PO_Number',
        })}
        {renderKeyValue('Currency', inv['Currency_Code'], req('Currency_Code'), {
          path: basePath,
          key: 'Currency_Code',
        })}
        {renderKeyValue('Payment Terms', inv['Payment_Terms'], false, {
          path: basePath,
          key: 'Payment_Terms',
        })}
      </div>
    );

    const parties = (
      <div className="grid grid-cols-1 gap-4">
        <div className="grid gap-2 p-3 rounded border bg-muted/10">
          <div className="font-medium">Vendor</div>
          {renderKeyValue('Name', inv['Vendor_Name'], req('Vendor_Name'), {
            path: basePath,
            key: 'Vendor_Name',
          })}
          {renderKeyValue(
            'Address',
            vendorAddress || inv['Vendor_Address'],
            req('Vendor_Address'),
            { path: basePath, key: 'Vendor_Address' },
          )}
          {renderKeyValue('Email', inv['Vendor_Email'], false, {
            path: basePath,
            key: 'Vendor_Email',
          })}
          {renderKeyValue('Phone', inv['Vendor_Phone'] ?? inv['Phone_Number'], false, {
            path: basePath,
            key: 'Vendor_Phone',
          })}
        </div>
        <div className="grid gap-2 p-3 rounded border bg-muted/10">
          <div className="font-medium">Bill To</div>
          {renderKeyValue('Name', inv['Bill_To_Name'], req('Bill_To_Name'), {
            path: basePath,
            key: 'Bill_To_Name',
          })}
          {renderKeyValue(
            'Address',
            billToAddress || inv['Bill_To_Address'],
            req('Bill_To_Address'),
            { path: basePath, key: 'Bill_To_Address' },
          )}
          {renderKeyValue('Email', inv['Bill_To_Email'], false, {
            path: basePath,
            key: 'Bill_To_Email',
          })}
          {renderKeyValue('Phone', inv['Phone_Number'], false, {
            path: basePath,
            key: 'Phone_Number',
          })}
        </div>
      </div>
    );

    const taxLines = Array.isArray(inv['Tax_Lines']) ? inv['Tax_Lines'] : null;
    const lineItems = Array.isArray(inv['Line_Items']) ? inv['Line_Items'] : null;

    const taxSection = taxLines
      ? renderEditableArrayTable(
          'Tax Lines',
          taxLines as any,
          ['Description', 'Rate', 'Amount'],
          { Description: 'Description', Rate: 'Taux', Amount: 'Montant' },
          { path: basePath, key: 'Tax_Lines' },
        )
      : null;

    const itemsSection = lineItems
      ? renderEditableArrayTable(
          'Items',
          lineItems as any,
          ['Description', 'QTY', 'Unit', 'Price', 'Amount'],
          {
            Description: 'Description',
            QTY: 'Qté',
            Unit: 'Unité',
            Price: 'Prix',
            Amount: 'Montant',
          },
          { path: basePath, key: 'Line_Items' },
        )
      : null;

    const totalsSection = (
      <div className="w-full p-3 rounded border bg-muted/10 max-w-full">
        <div className="grid grid-cols-1 gap-2">
          {renderKeyValue('Subtotal', inv['Subtotal'], req('Subtotal'), {
            path: basePath,
            key: 'Subtotal',
          })}
          {renderKeyValue('Tax', inv['Tax'], req('Tax'), {
            path: basePath,
            key: 'Tax',
          })}
          {renderKeyValue('Shipping', inv['Shipping'], req('Shipping'), {
            path: basePath,
            key: 'Shipping',
          })}
          {renderKeyValue('Discount', inv['Discount'], req('Discount'), {
            path: basePath,
            key: 'Discount',
          })}
          {renderKeyValue('Total', inv['Total'], req('Total'), {
            path: basePath,
            key: 'Total',
          })}
          {renderKeyValue('Balance Due', inv['Balance_Due'], req('Balance_Due'), {
            path: basePath,
            key: 'Balance_Due',
          })}
        </div>
      </div>
    );

    return (
      <div className="grid gap-6 w-full max-w-full">
        {header}
        {parties}
        {taxSection}
        {itemsSection}
        {totalsSection}
      </div>
    );
  };

  const renderBankStatementLayout = (data: Record<string, any>): React.ReactNode => {
    const header = (
      <div className="grid grid-cols-1 gap-4">
        {renderKeyValue(
          'Account Holder',
          getFirst(data, [['account', 'holder'], ['holder']]),
          true,
          resolveFirstSource(data, [['account', 'holder'], ['holder']]),
        )}
        {renderKeyValue(
          'Account Number',
          getFirst(data, [['account', 'number']]),
          true,
          resolveFirstSource(data, [['account', 'number']]),
        )}
        {renderKeyValue(
          'IBAN',
          getFirst(data, [['iban']]),
          false,
          resolveFirstSource(data, [['iban']]),
        )}
        {renderKeyValue(
          'BIC',
          getFirst(data, [['bic'], ['swift']]),
          false,
          resolveFirstSource(data, [['bic'], ['swift']]),
        )}
        {renderKeyValue(
          'Start Date',
          getFirst(data, [
            ['period', 'start'],
            ['start', 'date'],
          ]),
          true,
          resolveFirstSource(data, [
            ['period', 'start'],
            ['start', 'date'],
          ]),
        )}
        {renderKeyValue(
          'End Date',
          getFirst(data, [
            ['period', 'end'],
            ['end', 'date'],
          ]),
          true,
          resolveFirstSource(data, [
            ['period', 'end'],
            ['end', 'date'],
          ]),
        )}
      </div>
    );
    const transactions = getFirst(data, [['transactions']]);
    const opening = getFirst(data, [['opening', 'balance']]);
    const closing = getFirst(data, [['closing', 'balance']]);
    const balances = (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderKeyValue(
          'Opening Balance',
          typeof opening === 'number' ? formatCurrency(opening) : opening,
          false,
          resolveFirstSource(data, [['opening', 'balance']]),
        )}
        {renderKeyValue(
          'Closing Balance',
          typeof closing === 'number' ? formatCurrency(closing) : closing,
          false,
          resolveFirstSource(data, [['closing', 'balance']]),
        )}
      </div>
    );
    const txSource = resolveFirstSource(data, [['transactions']]);
    const txSection = Array.isArray(transactions)
      ? txSource
        ? renderEditableArrayTable(
            'Transactions',
            transactions as any,
            ['date', 'description', 'debit', 'credit', 'amount', 'balance', 'type'],
            {
              date: 'Date',
              description: 'Libellé',
              debit: 'Débit',
              credit: 'Crédit',
              amount: 'Montant',
              balance: 'Solde',
              type: 'Type',
            },
            txSource,
          )
        : renderDataTable(
            'Transactions',
            transactions as any,
            ['date', 'description', 'debit', 'credit', 'amount', 'balance', 'type'],
            {
              date: 'Date',
              description: 'Libellé',
              debit: 'Débit',
              credit: 'Crédit',
              amount: 'Montant',
              balance: 'Solde',
              type: 'Type',
            },
          )
      : null;

    return (
      <div className="grid gap-6 w-full max-w-full">
        {header}
        {txSection}
        {balances}
      </div>
    );
  };

  const renderPayslipLayout = (data: Record<string, any>): React.ReactNode => {
    const usedKeys = new Set<string>();
    const markUsed = (k: string) => usedKeys.add(normalize(k));

    const header = (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(() => {
          const v = getFirst(data, [['employee', 'name'], ['name']]);
          if (v !== undefined) markUsed('employee_name');
          return renderKeyValue(
            'Employé',
            v,
            true,
            resolveFirstSource(data, [['employee', 'name'], ['name']]),
          );
        })()}
        {(() => {
          const v = getFirst(data, [['employee', 'id'], ['id']]);
          if (v !== undefined) markUsed('employee_id');
          return renderKeyValue(
            'Identifiant',
            v,
            false,
            resolveFirstSource(data, [['employee', 'id'], ['id']]),
          );
        })()}
        {(() => {
          const s = getFirst(data, [['period', 'start']]);
          const e = getFirst(data, [['period', 'end']]);
          if (s !== undefined) markUsed('period_start');
          if (e !== undefined) markUsed('period_end');
          return renderKeyValue(
            'Période',
            `${s ?? ''} — ${e ?? ''}`,
            true,
            resolveFirstSource(data, [['period', 'start']]),
          );
        })()}
      </div>
    );
    const deductions = getFirst(data, [['deductions']]);
    if (deductions !== undefined) markUsed('deductions');
    const gross = getFirst(data, [['gross', 'pay']]);
    if (gross !== undefined) markUsed('gross_pay');
    const net = getFirst(data, [['net', 'pay']]);
    if (net !== undefined) markUsed('net_pay');

    const totals = (
      <div className="grid grid-cols-1 gap-4">
        {renderKeyValue(
          'Salaire brut',
          typeof gross === 'number' ? formatCurrency(gross) : gross,
          true,
          resolveFirstSource(data, [['gross', 'pay']]),
        )}
        {renderKeyValue(
          'Salaire net',
          typeof net === 'number' ? formatCurrency(net) : net,
          true,
          resolveFirstSource(data, [['net', 'pay']]),
        )}
      </div>
    );

    const dedSrc = resolveFirstSource(data, [['deductions']]);
    const dedSection = Array.isArray(deductions)
      ? dedSrc
        ? renderEditableArrayTable(
            'Deductions',
            deductions as any,
            ['type', 'amount'],
            { type: 'Type', amount: 'Montant' },
            dedSrc,
          )
        : renderDataTable('Deductions', deductions as any, ['type', 'amount'], {
            type: 'Type',
            amount: 'Montant',
          })
      : null;

    // Optional additional arrays commonly present
    const earnings = getFirst(data, [['earnings'], ['earning', 'items']]);
    if (earnings !== undefined) markUsed('earnings');
    const allowances = getFirst(data, [['allowances']]);
    if (allowances !== undefined) markUsed('allowances');
    const contributions = getFirst(data, [['contributions']]);
    if (contributions !== undefined) markUsed('contributions');
    const taxes = getFirst(data, [['taxes'], ['withholdings']]);
    if (taxes !== undefined) markUsed('taxes');

    const earnSrc = resolveFirstSource(data, [['earnings'], ['earning', 'items']]);
    const allowSrc = resolveFirstSource(data, [['allowances']]);
    const contribSrc = resolveFirstSource(data, [['contributions']]);
    const taxesSrc = resolveFirstSource(data, [['taxes'], ['withholdings']]);
    const earningsSection = Array.isArray(earnings)
      ? earnSrc
        ? renderEditableArrayTable(
            'Gains',
            earnings as any,
            ['type', 'hours', 'rate', 'amount'],
            { type: 'Type', hours: 'Heures', rate: 'Taux', amount: 'Montant' },
            earnSrc,
          )
        : renderDataTable('Gains', earnings as any, ['type', 'hours', 'rate', 'amount'], {
            type: 'Type',
            hours: 'Heures',
            rate: 'Taux',
            amount: 'Montant',
          })
      : null;
    const allowancesSection = Array.isArray(allowances)
      ? allowSrc
        ? renderEditableArrayTable(
            'Allocations',
            allowances as any,
            ['type', 'amount'],
            { type: 'Type', amount: 'Montant' },
            allowSrc,
          )
        : renderDataTable('Allocations', allowances as any, ['type', 'amount'], {
            type: 'Type',
            amount: 'Montant',
          })
      : null;
    const contributionsSection = Array.isArray(contributions)
      ? contribSrc
        ? renderEditableArrayTable(
            'Cotisations',
            contributions as any,
            ['type', 'amount'],
            { type: 'Type', amount: 'Montant' },
            contribSrc,
          )
        : renderDataTable('Cotisations', contributions as any, ['type', 'amount'], {
            type: 'Type',
            amount: 'Montant',
          })
      : null;
    const taxesSection = Array.isArray(taxes)
      ? taxesSrc
        ? renderEditableArrayTable(
            'Impôts',
            taxes as any,
            ['type', 'amount'],
            { type: 'Type', amount: 'Montant' },
            taxesSrc,
          )
        : renderDataTable('Impôts', taxes as any, ['type', 'amount'], {
            type: 'Type',
            amount: 'Montant',
          })
      : null;

    // Render remaining top-level or nested `data` fields not shown yet
    const renderRemaining = () => {
      const blocks: Array<React.ReactNode> = [];
      const pushEntries = (obj: Record<string, any>) => {
        Object.entries(obj).forEach(([k, v]) => {
          const nk = normalize(k);
          if (['data'].includes(k)) return;
          if (usedKeys.has(nk)) return;
          blocks.push(
            <div key={`rest-${k}`} className="grid gap-1 min-w-0">
              {renderKeyValue(humanizeKey(k), v, false, { path: [], key: k })}
            </div>,
          );
        });
      };
      pushEntries(data);
      if (
        data &&
        typeof data === 'object' &&
        (data as any).data &&
        typeof (data as any).data === 'object'
      ) {
        Object.entries((data as any).data).forEach(([k, v]) => {
          blocks.push(
            <div key={`rest-data-${k}`} className="grid gap-1 min-w-0">
              {renderKeyValue(humanizeKey(k), v, false, {
                path: ['data'],
                key: k,
              })}
            </div>,
          );
        });
      }
      if (blocks.length === 0) return null;
      return <div className="grid grid-cols-1 gap-3">{blocks}</div>;
    };

    return (
      <div className="grid gap-6 w-full max-w-full">
        {header}
        {earningsSection}
        {dedSection}
        {allowancesSection}
        {contributionsSection}
        {taxesSection}
        {totals}
        {renderRemaining()}
      </div>
    );
  };

  const renderFinancialStatementLayout = (data: Record<string, any>): React.ReactNode => {
    const header = (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderKeyValue(
          'Statement Type',
          getFirst(data, [['statement', 'type']]),
          true,
          resolveFirstSource(data, [['statement', 'type']]),
        )}
        {renderKeyValue(
          'Start Date',
          getFirst(data, [['period', 'start']]),
          true,
          resolveFirstSource(data, [['period', 'start']]),
        )}
        {renderKeyValue(
          'End Date',
          getFirst(data, [['period', 'end']]),
          true,
          resolveFirstSource(data, [['period', 'end']]),
        )}
      </div>
    );
    const lineItems = getFirst(data, [['line', 'items']]);
    const src = resolveFirstSource(data, [['line', 'items']]);
    const items = Array.isArray(lineItems)
      ? src
        ? renderEditableArrayTable(
            'Line Items',
            lineItems as any,
            ['name', 'value'],
            { name: 'Nom', value: 'Valeur' },
            src,
          )
        : renderDataTable('Line Items', lineItems as any, ['name', 'value'], {
            name: 'Nom',
            value: 'Valeur',
          })
      : null;
    return (
      <div className="grid gap-6 w-full max-w-full">
        {header}
        {items}
      </div>
    );
  };

  const renderPurchaseOrderLayout = (data: Record<string, any>): React.ReactNode => {
    const header = (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderKeyValue(
          'PO Number',
          getFirst(data, [['po', 'number']]),
          true,
          resolveFirstSource(data, [['po', 'number']]),
        )}
        {renderKeyValue(
          'Date',
          getFirst(data, [['date']]),
          true,
          resolveFirstSource(data, [['date']]),
        )}
        {renderKeyValue(
          'Buyer',
          getFirst(data, [['buyer']]),
          true,
          resolveFirstSource(data, [['buyer']]),
        )}
        {renderKeyValue(
          'Supplier',
          getFirst(data, [['supplier']]),
          true,
          resolveFirstSource(data, [['supplier']]),
        )}
        {renderKeyValue(
          'Currency',
          getFirst(data, [['currency']]),
          false,
          resolveFirstSource(data, [['currency']]),
        )}
        {renderKeyValue(
          'Total Amount',
          getFirst(data, [['total', 'amount']]),
          false,
          resolveFirstSource(data, [['total', 'amount']]),
        )}
      </div>
    );
    const items = getFirst(data, [['items']]);
    const src = resolveFirstSource(data, [['items']]);
    const itemsTable = Array.isArray(items)
      ? src
        ? renderEditableArrayTable(
            'Items',
            items as any,
            ['description', 'quantity', 'unit_price', 'amount'],
            {
              description: 'Description',
              quantity: 'Qté',
              unit_price: 'Prix unitaire',
              amount: 'Montant',
            },
            src,
          )
        : renderDataTable(
            'Items',
            items as any,
            ['description', 'quantity', 'unit_price', 'amount'],
            {
              description: 'Description',
              quantity: 'Qté',
              unit_price: 'Prix unitaire',
              amount: 'Montant',
            },
          )
      : null;
    return (
      <div className="grid gap-6 w-full max-w-full">
        {header}
        {itemsTable}
      </div>
    );
  };

  const renderContractLayout = (data: Record<string, any>): React.ReactNode => {
    const header = (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderKeyValue(
          'Contract ID',
          getFirst(data, [['contract', 'id']]),
          true,
          resolveFirstSource(data, [['contract', 'id']]),
        )}
        {renderKeyValue(
          'Effective Date',
          getFirst(data, [['effective', 'date']]),
          true,
          resolveFirstSource(data, [['effective', 'date']]),
        )}
        {renderKeyValue(
          'Expiry Date',
          getFirst(data, [['expiry', 'date']]),
          false,
          resolveFirstSource(data, [['expiry', 'date']]),
        )}
        {renderKeyValue(
          'Terms',
          getFirst(data, [['terms']]),
          true,
          resolveFirstSource(data, [['terms']]),
        )}
      </div>
    );
    const parties = getFirst(data, [['parties']]);
    const src = resolveFirstSource(data, [['parties']]);
    const partiesTable = Array.isArray(parties)
      ? src
        ? renderEditableArrayTable(
            'Parties',
            parties as any,
            ['name', 'role', 'address'],
            { name: 'Nom', role: 'Rôle', address: 'Adresse' },
            src,
          )
        : renderDataTable('Parties', parties as any, ['name', 'role', 'address'], {
            name: 'Nom',
            role: 'Rôle',
            address: 'Adresse',
          })
      : null;
    return (
      <div className="grid gap-6 w-full max-w-full">
        {header}
        {partiesTable}
      </div>
    );
  };

  const renderCheckLayout = (data: Record<string, any>): React.ReactNode => {
    const header = (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderKeyValue(
          'Check Number',
          getFirst(data, [['check', 'number']]),
          true,
          resolveFirstSource(data, [['check', 'number']]),
        )}
        {renderKeyValue(
          'Date',
          getFirst(data, [['date']]),
          true,
          resolveFirstSource(data, [['date']]),
        )}
        {renderKeyValue(
          'Payer',
          getFirst(data, [['payer']]),
          true,
          resolveFirstSource(data, [['payer']]),
        )}
        {renderKeyValue(
          'Payee',
          getFirst(data, [['payee']]),
          true,
          resolveFirstSource(data, [['payee']]),
        )}
        {renderKeyValue(
          'Bank',
          getFirst(data, [['bank']]),
          false,
          resolveFirstSource(data, [['bank']]),
        )}
        {renderKeyValue(
          'Amount',
          getFirst(data, [['amount']]),
          true,
          resolveFirstSource(data, [['amount']]),
        )}
        {renderKeyValue(
          'Currency',
          getFirst(data, [['currency']]),
          false,
          resolveFirstSource(data, [['currency']]),
        )}
      </div>
    );
    return <div className="grid gap-6 w-full max-w-full">{header}</div>;
  };

  const renderTaxFormLayout = (data: Record<string, any>): React.ReactNode => {
    const header = (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderKeyValue(
          'Form Type',
          getFirst(data, [['form', 'type']]),
          true,
          resolveFirstSource(data, [['form', 'type']]),
        )}
        {renderKeyValue(
          'Tax Year',
          getFirst(data, [['tax', 'year']]),
          true,
          resolveFirstSource(data, [['tax', 'year']]),
        )}
        {renderKeyValue(
          'Taxpayer Name',
          getFirst(data, [['taxpayer', 'name']]),
          true,
          resolveFirstSource(data, [['taxpayer', 'name']]),
        )}
      </div>
    );
    const fieldsSrc = resolveFirstSource(data, [['fields']]);
    const fields = getFirst(data, [['fields']]);
    const fieldsBlock =
      fields && typeof fields === 'object' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Object.entries(fields as Record<string, any>).map(([k, v]) => (
            <div key={`field-${k}`} className="grid gap-1 min-w-0">
              {renderKeyValue(humanizeKey(k), v, false, {
                path: [...(fieldsSrc?.path ?? []), ...(fieldsSrc ? [fieldsSrc.key] : [])],
                key: k,
              })}
            </div>
          ))}
        </div>
      ) : null;
    return (
      <div className="grid gap-6 w-full max-w-full">
        {header}
        {fieldsBlock}
      </div>
    );
  };

  const renderByType = (): React.ReactNode | null => {
    if (!editedMetadata || typeof editedMetadata !== 'object') return null;
    const data = editedMetadata as Record<string, any>;
    const type = (documentType || '').toUpperCase();
    if (type.includes('INVOICE')) return renderInvoiceLayout(data);
    if (type.includes('BANK')) return renderBankStatementLayout(data);
    if (type.includes('PAYSLIP')) return renderPayslipLayout(data);
    if (type.includes('FINANCIAL_STATEMENT')) return renderFinancialStatementLayout(data);
    if (type.includes('PURCHASE_ORDER')) return renderPurchaseOrderLayout(data);
    if (type.includes('CONTRACT')) return renderContractLayout(data);
    if (type.includes('CHECK')) return renderCheckLayout(data);
    if (type.includes('TAX_FORM')) return renderTaxFormLayout(data);
    return null;
  };

  return (
    <Card>
      <CardContent className="grid gap-4 min-w-0">
        <div className="flex items-center justify-between p-3 rounded border bg-muted/10">
          <div className="flex items-baseline gap-2">
            <Label>Type</Label>
            <div className="text-sm font-medium" title={documentType || '—'}>
              {documentType || '—'}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">{entries.length} champs</div>
        </div>

        {entries.length === 0 && (
          <div className="text-sm text-muted-foreground">Aucune métadonnée disponible.</div>
        )}

        {renderByType()}

        {entries.length > 0 && !renderByType() && (
          <div className="grid grid-cols-1 gap-3 min-w-0">
            {entries.map(({ key, value }) => (
              <div key={key} className={`grid gap-1 min-w-0`}>
                {renderKeyValue(humanizeKey(key), value, false, {
                  path: [],
                  key,
                })}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
